import { Button, Empty, Table, Tag, Tooltip, Typography } from "antd";
import { CheckOutlined, CloseOutlined } from "@ant-design/icons";
import { useCallback, useEffect, useMemo, useState } from "react";
import type { ColumnsType } from "antd/es/table";
import { LeaveRequest, LeaveRequestPending } from "../utils/types";
import {
  fixedTableScrollY,
  formatEnumLabel,
  getRequestStatusType,
  Notify,
  RequestStatus,
} from "../utils/constants";
import { apiClient } from "../utils/httpClient";
import { useUser } from "../context/UserContext";
import RequestStatusModal from "../components/Modals/RequestStatusModal";
import DocumentReviewModal from "../components/Modals/DocumentReviewModal";
import { ViewDocumentsBtn } from "../components/Buttons/ViewDocumentsBtn";
import { RequestIdLink } from "../components/Buttons/RequestIdLink";
import { formatRequestedAt } from "../utils/helpers";
import {
  activeColumn,
  DEFAULT_EMPLOYEE_STATUS_FILTER,
  EmployeeStatusFilter,
  EmployeeStatusFilterValue,
  matchesEmployeeStatus,
} from "../components/Filters/EmployeeStatusFilter";

const PendingAdministrativRequests = () => {
  const { authUser } = useUser();
  const [requests, setRequests] = useState<LeaveRequest[]>([]);
  const [cancellations, setCancellations] = useState<LeaveRequestPending[]>(
    [],
  );
  const [documentReviews, setDocumentReviews] = useState<LeaveRequest[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [selectedRequest, setSelectedRequest] = useState<
    LeaveRequest | LeaveRequestPending | null
  >(null);
  const [selectedRequestStatus, setSelectedRequestStatus] =
    useState<RequestStatus>(RequestStatus.APPROVE);
  const [isRequestStatusOpen, setIsRequestStatusOpen] =
    useState<boolean>(false);
  const [documentReviewRequest, setDocumentReviewRequest] =
    useState<LeaveRequest | null>(null);
  const [isDocumentReviewOpen, setIsDocumentReviewOpen] =
    useState<boolean>(false);
  // One Active filter per table, each defaulting to Yes.
  const [employeeStatusFilter, setEmployeeStatusFilter] =
    useState<EmployeeStatusFilterValue>(DEFAULT_EMPLOYEE_STATUS_FILTER);
  const [cancellationStatusFilter, setCancellationStatusFilter] =
    useState<EmployeeStatusFilterValue>(DEFAULT_EMPLOYEE_STATUS_FILTER);
  const [documentReviewStatusFilter, setDocumentReviewStatusFilter] =
    useState<EmployeeStatusFilterValue>(DEFAULT_EMPLOYEE_STATUS_FILTER);

  const visibleRequests = useMemo(
    () =>
      requests.filter((r) =>
        matchesEmployeeStatus(r.employeeStatus, employeeStatusFilter),
      ),
    [requests, employeeStatusFilter],
  );
  const visibleCancellations = useMemo(
    () =>
      cancellations.filter((r) =>
        matchesEmployeeStatus(r.employeeStatus, cancellationStatusFilter),
      ),
    [cancellations, cancellationStatusFilter],
  );
  const visibleDocumentReviews = useMemo(
    () =>
      documentReviews.filter((r) =>
        matchesEmployeeStatus(r.employeeStatus, documentReviewStatusFilter),
      ),
    [documentReviews, documentReviewStatusFilter],
  );

  const fetchPendingRequests = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await apiClient.get(
        `${process.env.REACT_APP_API_URL}/getRequestsPendingAdministrativ`,
      );
      setRequests(response.data);
    } catch (error) {
      console.error("Failed to fetch pending Administrativ requests:", error);
      Notify("error", "Could not load pending requests.");
    } finally {
      setIsLoading(false);
    }
  }, []);

  const fetchPendingCancellations = useCallback(async () => {
    try {
      const response = await apiClient.get(
        `${process.env.REACT_APP_API_URL}/cancellation-requests/pending-administrativ`,
      );
      setCancellations(response.data);
    } catch (error) {
      console.error("Failed to fetch pending Administrativ cancellations:", error);
      Notify("error", "Could not load pending cancellations.");
    }
  }, []);

  const fetchPendingDocumentReviews = useCallback(async () => {
    try {
      const response = await apiClient.get(
        `${process.env.REACT_APP_API_URL}/leave-requests/pending-document-review`,
      );
      setDocumentReviews(response.data);
    } catch (error) {
      console.error("Failed to fetch pending document reviews:", error);
      Notify("error", "Could not load pending document reviews.");
    }
  }, []);

  useEffect(() => {
    if (!authUser) return;
    fetchPendingRequests();
    fetchPendingCancellations();
    fetchPendingDocumentReviews();
  }, [authUser, fetchPendingRequests, fetchPendingCancellations, fetchPendingDocumentReviews]);

  useEffect(() => {
    if (!isRequestStatusOpen && authUser) {
      fetchPendingRequests();
      fetchPendingCancellations();
    }
  }, [authUser, fetchPendingRequests, fetchPendingCancellations, isRequestStatusOpen]);

  useEffect(() => {
    if (!isDocumentReviewOpen && authUser) {
      fetchPendingDocumentReviews();
    }
  }, [authUser, fetchPendingDocumentReviews, isDocumentReviewOpen]);

  const columns: ColumnsType<LeaveRequest> = [
    {
      title: "Requested at",
      dataIndex: "requestCreationDate",
      key: "requestCreationDate",
      render: (value: LeaveRequest["requestCreationDate"]) =>
        formatRequestedAt(value),
    },
    {
      title: "Request ID",
      dataIndex: "id",
      key: "id",
      render: (_: unknown, record: LeaveRequest) => (
        <RequestIdLink id={record.id} />
      ),
    },
    {
      title: "Name",
      dataIndex: "employeeName",
      key: "employeeName",
    },
    activeColumn<LeaveRequest>(),
    {
      title: "Request Type",
      dataIndex: "requestType",
      key: "requestType",
      render: (value: LeaveRequest["requestType"]) => formatEnumLabel(value),
    },
    {
      title: "Start Date",
      dataIndex: "startDate",
      key: "startDate",
    },
    {
      title: "End Date",
      dataIndex: "endDate",
      key: "endDate",
    },
    {
      title: "Comment",
      dataIndex: "comment",
      key: "comment",
      ellipsis: true,
    },
    {
      title: "Status",
      dataIndex: "requestStatus",
      key: "requestStatus",
      render: (value: LeaveRequest["requestStatus"]) => (
        <Tag className={getRequestStatusType(value)}>
          {formatEnumLabel(value)}
        </Tag>
      ),
    },
    {
      title: "Document",
      key: "document",
      render: (_: unknown, record: LeaveRequest) => (
        <div className="flex flex-row items-center gap-1">
          {record.documentOverdue ? (
            <Tag color="red">Overdue</Tag>
          ) : record.documentDeadline ? (
            <Tag color="default">Due {record.documentDeadline}</Tag>
          ) : null}
          <ViewDocumentsBtn documents={record.documents} />
        </div>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      fixed: "right",
      width: 120,
      render: (_: unknown, leaveRequest: LeaveRequest) => (
        <div className="flex flex-row gap-2">
          <Tooltip title="Approve request">
            <Button
              type="text"
              className="status-action-btn"
              icon={<CheckOutlined />}
              onClick={() => {
                setSelectedRequest(leaveRequest);
                setSelectedRequestStatus(RequestStatus.APPROVE);
                setIsRequestStatusOpen(true);
              }}
            />
          </Tooltip>
          <Tooltip title="Reject request">
            <Button
              type="text"
              className="status-action-btn"
              danger
              icon={<CloseOutlined />}
              onClick={() => {
                setSelectedRequest(leaveRequest);
                setSelectedRequestStatus(RequestStatus.REJECT);
                setIsRequestStatusOpen(true);
              }}
            />
          </Tooltip>
        </div>
      ),
    },
  ];

  const cancellationColumns: ColumnsType<LeaveRequestPending> = [
    {
      title: "Requested at",
      dataIndex: "creationDate",
      key: "creationDate",
      render: (value: string) => formatRequestedAt(value),
    },
    {
      title: "Request ID",
      dataIndex: "originalRequestId",
      key: "originalRequestId",
      render: (_: unknown, record: LeaveRequestPending) => (
        <RequestIdLink id={record.originalRequestId} cancellation />
      ),
    },
    {
      title: "Name",
      dataIndex: "employeeName",
      key: "employeeName",
    },
    activeColumn<LeaveRequestPending>(),
    {
      title: "Request Type",
      dataIndex: "requestType",
      key: "requestType",
      render: (value?: string) => (value ? formatEnumLabel(value) : "-"),
    },
    {
      title: "Start Date",
      dataIndex: "startDate",
      key: "startDate",
    },
    {
      title: "End Date",
      dataIndex: "endDate",
      key: "endDate",
    },
    {
      title: "Duration (days)",
      dataIndex: "duration",
      key: "duration",
    },
    {
      title: "Comment",
      dataIndex: "comment",
      key: "comment",
      ellipsis: true,
    },
    {
      title: "Actions",
      key: "actions",
      fixed: "right",
      width: 120,
      render: (_: unknown, cancellation: LeaveRequestPending) => (
        <div className="flex flex-row gap-2">
          <Tooltip title="Approve cancellation">
            <Button
              type="text"
              className="status-action-btn"
              icon={<CheckOutlined />}
              onClick={() => {
                setSelectedRequest(cancellation);
                setSelectedRequestStatus(RequestStatus.APPROVE);
                setIsRequestStatusOpen(true);
              }}
            />
          </Tooltip>
          <Tooltip title="Reject cancellation">
            <Button
              type="text"
              className="status-action-btn"
              danger
              icon={<CloseOutlined />}
              onClick={() => {
                setSelectedRequest(cancellation);
                setSelectedRequestStatus(RequestStatus.REJECT);
                setIsRequestStatusOpen(true);
              }}
            />
          </Tooltip>
        </div>
      ),
    },
  ];

  const documentReviewColumns: ColumnsType<LeaveRequest> = [
    {
      title: "Name",
      dataIndex: "employeeName",
      key: "employeeName",
    },
    activeColumn<LeaveRequest>(),
    {
      title: "Request ID",
      dataIndex: "id",
      key: "id",
      render: (_: unknown, record: LeaveRequest) => (
        <RequestIdLink id={record.id} />
      ),
    },
    {
      title: "Donation date",
      dataIndex: "eventDate",
      key: "eventDate",
    },
    {
      title: "Latest document",
      key: "document",
      render: (_: unknown, record: LeaveRequest) => {
        const latest = record.documents?.[0];
        if (!latest) return <Tag color="orange">Not uploaded yet</Tag>;
        return (
          <>
            <span>{latest.fileName || "(no file name)"}</span>{" "}
            <Tag color={latest.reviewStatus === "REJECTED" ? "red" : "default"}>
              {latest.reviewStatus}
            </Tag>
          </>
        );
      },
    },
    {
      title: "Uploaded at",
      key: "uploadedAt",
      render: (_: unknown, record: LeaveRequest) =>
        formatRequestedAt(record.documents?.[0]?.uploadedAt),
    },
    {
      title: "Actions",
      key: "actions",
      fixed: "right",
      width: 140,
      render: (_: unknown, record: LeaveRequest) => (
        <Button
          type="primary"
          size="small"
          onClick={() => {
            setDocumentReviewRequest(record);
            setIsDocumentReviewOpen(true);
          }}
        >
          Review document
        </Button>
      ),
    },
  ];

  return (
    <div className="w-full">
      <Typography.Title level={3}>Pending Administrativ Review</Typography.Title>
      <div className="ui divider"></div>
      <EmployeeStatusFilter
        className="mb-3"
        value={employeeStatusFilter}
        onChange={setEmployeeStatusFilter}
      />

      <RequestStatusModal
        isOpen={isRequestStatusOpen}
        setIsOpen={setIsRequestStatusOpen}
        request={selectedRequest}
        status={selectedRequestStatus}
        source={"default"}
        onCancelConfirm={() => {
          fetchPendingRequests();
          fetchPendingCancellations();
        }}
      />

      <Table
        columns={columns}
        dataSource={visibleRequests}
        loading={isLoading}
        rowKey={(record) => record.id}
        scroll={{ x: true, y: fixedTableScrollY }}
        locale={{
          emptyText: <Empty description="No requests pending Administrativ review." />,
        }}
      />

      <Typography.Title level={4} style={{ marginTop: 32 }}>
        Pending Cancellation Review
      </Typography.Title>
      <EmployeeStatusFilter
        className="mb-3"
        value={cancellationStatusFilter}
        onChange={setCancellationStatusFilter}
      />

      <Table
        columns={cancellationColumns}
        dataSource={visibleCancellations}
        rowKey={(record) => record.id}
        scroll={{ x: true, y: fixedTableScrollY }}
        locale={{
          emptyText: <Empty description="No cancellations pending Administrativ review." />,
        }}
      />

      <Typography.Title level={4} style={{ marginTop: 32 }}>
        Pending Document Review
      </Typography.Title>
      <EmployeeStatusFilter
        className="mb-3"
        value={documentReviewStatusFilter}
        onChange={setDocumentReviewStatusFilter}
      />

      <DocumentReviewModal
        isOpen={isDocumentReviewOpen}
        setIsOpen={setIsDocumentReviewOpen}
        request={documentReviewRequest}
        onDone={fetchPendingDocumentReviews}
      />

      <Table
        columns={documentReviewColumns}
        dataSource={visibleDocumentReviews}
        rowKey={(record) => record.id}
        scroll={{ x: true, y: fixedTableScrollY }}
        locale={{
          emptyText: <Empty description="No documents pending Administrativ review." />,
        }}
      />
    </div>
  );
};

export default PendingAdministrativRequests;
